import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import {
    AdalService,
    AsyncLocalStorageService,
    LoggerService,
} from '@kpmg-tax/core';
import { ModalFactoryService, WindowRef } from '@kpmg-tax/ui';
//import { AppInsightsService } from '@kpmg-tax/app-insights';

import {
    ActivityMonitorService,
    ActivityMonitorConfig,
    ConfigChangeRequest,
} from '../activity-monitor';
import { MsalService } from '@azure/msal-angular';
import { AppContextStore, STORAGEKEY_ACTIVITY_MONITOR_CONFIG_OVERRIDE, STORAGEKEY_IMPERSONATE } from '../../core/services/app-context';
import { SessionMgmtService } from '.';
import { SessionService } from '../session-service/session.service';
import { environment } from '../../../environments/environment';
import { EndSessionRequest } from '@azure/msal-browser';

declare var $: any;

@Component({
    selector: 'session-mgmt',
    templateUrl: './session-mgmt.component.html',
})
export class SessionMgmtComponent implements OnInit, OnDestroy {
    private _subscriptions: Subscription[] = [];

    isSessionTimedOut: boolean;
    isWarningVisible: boolean;
    isExtendingSession: boolean;
    isLogoutPending: boolean;
    countdown: number;
    countdownText: string;
    countdownInterval: any;

    private _activityMonintorConfig: ActivityMonitorConfig;

    constructor(
        private _appContextStore: AppContextStore,
        //private _appInsightsService: AppInsightsService,
        private _asyncStorage: AsyncLocalStorageService,
        private _activityMonitorService: ActivityMonitorService,
        private _sessionMgmtService: SessionMgmtService,
        private _msalService: MsalService,
        private _modalFactoryService: ModalFactoryService,
        private _loggerService: LoggerService,
        private _windowRef: WindowRef,
        public sessionService: SessionService,
    ) {
        this.isSessionTimedOut = false;
        this.isWarningVisible = false;
        this.isExtendingSession = false;
        this.isLogoutPending = false;
        this.countdownInterval = null;
        this.countdown = 0;
        this.countdownText = '';

        this._activityMonintorConfig = environment.activityMonitor;
    }

    ngOnInit(): void {
        // Check for overriding activity monitor settings in Dev and QA environments
        if (!environment.production) {
            // Load activity monitor config (if overriden in local storage)
            this._asyncStorage
                .getItemAsync(STORAGEKEY_ACTIVITY_MONITOR_CONFIG_OVERRIDE)
                .subscribe(config => {
                    this._activityMonintorConfig =
                        JSON.parse(config) || environment.activityMonitor;

                    const changeRequest: ConfigChangeRequest = {
                        isEventSource: true,
                        config: this._activityMonintorConfig,
                    };

                    this._activityMonitorService.changeConfig(changeRequest);

                    // Register for activity config events since we are allowed to modify the settings.
                    this._subscriptions.push(
                        this._activityMonitorService.configChanged$.subscribe(
                            changeRequest => {
                                this._loggerService.trace(
                                    `SessionMgmtComponent -  EVENT: Activity Monitor Config Change. changeRequest: ${JSON.stringify(
                                        changeRequest,
                                    )}`,
                                );
                                this._activityMonintorConfig = changeRequest.config;

                                if (changeRequest.isEventSource) {
                                    this._loggerService.trace(
                                        `SessionMgmtComponent -  Notifying other windows/tabs of Activity Monitor Config Change event.`,
                                    );
                                    // Since this app is the source of this event, notify other windows/tabs so that they can update their config and reset timers.
                                    //this._appContextStore.activityConfigChanged(changeRequest.config);
                                }
                            },
                        ),
                    );
                });
        } else {
            // Remove the overriding activity monitor settings in environments outside of Dev and QA
            //if (this.envSettings.preferences.componentMode !== 'Dev' && this.envSettings.preferences.componentMode !== 'SQA') {
            this._asyncStorage.removeItem(
                STORAGEKEY_ACTIVITY_MONITOR_CONFIG_OVERRIDE,
            );
            //}
        }

        // Session activity events
        this._subscriptions.push(
            this._activityMonitorService.activityDetected$.subscribe(
                isEventSource => {
                    if (isEventSource) {
                        this._loggerService.trace(
                            `SessionMgmtComponent -  EVENT: Activity Detected in this app.`,
                        );
                        // Since this app is the source of this event, notify other windows/tabs so that they can reset their timers.
                        // This needs to be sent asynchronously because Internet Explorer will block logout events caused by the
                        // user context menu.
                        this._loggerService.trace(
                            `SessionMgmtComponent -  Notifying other windows/tabs of activity event.`,
                        );
                        this._windowRef.nativeWindow.setTimeout(() => {
                            this._appContextStore.sessionActivity();
                        }, 250);
                    }

                    if (this.isWarningVisible) {
                        this.extendSession();
                    }
                },
            ),
        );

        // Session heartbeat events
        this._subscriptions.push(
            this._activityMonitorService.heartbeat$.subscribe(isEventSource => {
                if (isEventSource && !this.isExtendingSession) {
                    this._loggerService.trace(
                        `SessionMgmtComponent -  EVENT: Heartbeat in this app. isEventSource: ${isEventSource}, isExtendingSession: ${this.isExtendingSession}`,
                    );
                    // Since this app is the source of this event, notify other windows/tabs so that they can
                    // reset their timers.
                    this._loggerService.trace(
                        `SessionMgmtComponent -  Notifying other windows/tabs of Heartbeat event.`,
                    );
                    this._appContextStore.sessionHeartbeat();
                }

                // This flag is reset here in order to prevent unnecessary heartbeat event from being sent when in the process
                // of extending a session and locking up the thread of execution in Chrome.
                this.isExtendingSession = false;
            }),
        );

        // Session warning events
        this._subscriptions.push(
            this._activityMonitorService.sessionWarning$.subscribe(isEventSource => {
                this._loggerService.trace(
                    `SessionMgmtComponent -  EVENT: Session Warning. isEventSource: ${isEventSource}, isExtendingSession: ${this.isExtendingSession}`,
                );
                this.showTimeoutWarning();

                if (isEventSource) {
                    // Since this app is the source of this event, notify other windows/tabs so that they can
                    // display their warning modal.
                    this._loggerService.trace(
                        `SessionMgmtComponent -  Notifying other windows/tabs of Session Warning event.`,
                    );
                    this._appContextStore.sessionWarning();
                }
            }),
        );

        // Session extension events from other tabs/windows
        this._subscriptions.push(
            this._activityMonitorService.sessionExtended$.subscribe(isEventSource => {
                // Checking the "isExtendingSession" flag will prevent the app from processing its own extension event when local storage is updated.
                if (!isEventSource && !this.isExtendingSession) {
                    this._loggerService.trace(
                        `SessionMgmtComponent -  EVENT: Session Extended from another tab/window. isExtendingSession: ${this.isExtendingSession}`,
                    );
                    this.stopCountdown();
                    this.hideTimeoutWarning();
                }
            }),
        );

        // Session Timeout events
        this._subscriptions.push(
            this._activityMonitorService.sessionTimeout$.subscribe(isEventSource => {
                this._loggerService.trace(
                    `SessionMgmtComponent -  EVENT: Session Timeout. isEventSource: ${isEventSource}, isExtendingSession: ${this.isExtendingSession}`,
                );

                this.isSessionTimedOut = true;
                this.isExtendingSession = false;
                this.stopCountdown();
                this.closeAllModals();

                // Broadcast session timeout event
                this._sessionMgmtService.sessionTimeout(isEventSource);

                // Kill session cookies/tokens
                this.logout();
            }),
        );

        // Logout pending events
        this._subscriptions.push(
            this._sessionMgmtService.logoutPending$.subscribe(isEventSource => {
                if (isEventSource) {
                    this._loggerService.trace(
                        `SessionMgmtComponent -  EVENT: Logout Pending in this app. isEventSource: ${isEventSource}`,
                    );
                    this.isLogoutPending = true;
                    this._appContextStore.pendingLogout();
                    this._modalFactoryService.showModal('#mdlLogout');
                } else {
                    // --- IMPORTANT ---
                    // This check is needed because if this instance of the app initiated the logout process, Internet Explorer
                    // will deliver the event caused by the local storage being updated.
                    if (!this.isLogoutPending) {
                        // We are only interested in this event when this navbar is NOT the source of the logout
                        this._loggerService.trace(
                            `SessionMgmtComponent -  EVENT: Logout Pending in another tab/window.`,
                        );
                        this._modalFactoryService.showModal('#mdlLogout');
                    }
                }
            }),
        );

        // Logout confirmed events from another tab/window
        this._subscriptions.push(
            this._sessionMgmtService.logoutConfirmed$.subscribe(isEventSource => {
                this.isLogoutPending = false;
                if (!isEventSource) {
                    this._loggerService.trace(
                        `SessionMgmtComponent -  EVENT: Logout Confirmed in another tab/window.`,
                    );
                    this.closeAllModals();
                }

                // Kill session cookies/tokens
                this.logout();
            }),
        );

        // Logout cancelled events from another tab/window
        this._subscriptions.push(
            this._sessionMgmtService.logoutCancelled$.subscribe(isEventSource => {
                this.isLogoutPending = false;
                if (!isEventSource) {
                    this._loggerService.trace(
                        `SessionMgmtComponent -  EVENT: Logout Cancelled.`,
                    );
                    this._modalFactoryService.hideModal('#mdlLogout');
                }
            }),
        );
    }

    ngOnDestroy(): void {
        this._subscriptions.forEach(s => s.unsubscribe());
    }

    showTimeoutWarning(): void {
        if (!this.isWarningVisible) {
            this.closeAllModals();
            this.startCountdown();
            this.isWarningVisible = true;
            this._modalFactoryService.showModal('#mdlTimeoutWarning');
        }
    }

    hideTimeoutWarning(): void {
        this.isWarningVisible = false;
        this._modalFactoryService.hideModal('#mdlTimeoutWarning');
    }

    extendSession(): void {
        this.isExtendingSession = true;
        this.stopCountdown();

        // Reset timers in other tabs/windows
        this._appContextStore.sessionExtended();

        // Reset timers in this app
        this._activityMonitorService.sessionExtended(true);
        this.hideTimeoutWarning();
    }

    onSessionExtended(): void {
        //this._appInsightsService.trackEvent({ name: 'SessionExtended' }, { component: 'SessionMgmtComponent' });
        this.extendSession();
    }

    confirmLogout(): void {
        // Stop listening for activity events
        this._activityMonitorService.monitorActivity(false);
        // Cancel impersonation
        this._asyncStorage.removeItem(STORAGEKEY_IMPERSONATE);
        // Notify all other tabs/windows.
        this._appContextStore.confirmLogout();

        this.closeAllModals();

        // Broadcast logout confirmation event internally
        this._sessionMgmtService.logoutConfirmed(true);
    }

    onLogoutConfirmed(): void {
        this.confirmLogout();
        //this._appInsightsService.trackEvent({ name: 'SessionLogoutConfirmed' }, { component: 'SessionMgmtComponent' });
        this.extendSession();
    }

    cancelLogout(): void {
        this.isLogoutPending = false;
        // Notify all other tabs/windows
        this._appContextStore.cancelLogout();
        // Dismiss modal
        this._modalFactoryService.hideModal('#mdlLogout');
    }

    onLogoutCancelled(): void {
        this.cancelLogout();
        //this._appInsightsService.trackEvent({ name: 'SessionLogoutCancelled' }, { component: 'SessionMgmtComponent' });
        this.extendSession();
    }

    private logout(): void {
        this.sessionService.clearSession();
        localStorage.clear();
        const activeAccount = this._msalService.instance.getActiveAccount();
        const logoutRequest: EndSessionRequest = {} as EndSessionRequest;
        logoutRequest.account = activeAccount;
        this._msalService.logoutRedirect(logoutRequest);
    }

    private startCountdown(): void {
        // Only start the countdown if it is not already running

        if (!this.countdownInterval) {
            this.countdown = this._activityMonintorConfig.sessionTimeout * 60;


            this.countdownText = this.formatToTimePhrase(this.countdown);

            this.countdownInterval = this._windowRef.nativeWindow.setInterval(() => {
                this.countdown--;
                this.countdownText = this.countdownText = this.formatToTimePhrase(
                    this.countdown,
                );
            }, 1000);
        }
    }

    private stopCountdown(): void {
        if (this.countdownInterval) {
            this._windowRef.nativeWindow.clearInterval(this.countdownInterval);
        }

        this.countdownInterval = null;
        this.countdown = 0;
        this.countdownText = '';
    }

    private formatToTimePhrase(seconds: number): string {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        const minPhrase = m === 1 ? 'minute' : 'minutes';
        const secPhrase = s === 1 ? 'second' : 'seconds';

        return m > 0 ? `${m} ${minPhrase} ${s} ${secPhrase}` : `${s} ${secPhrase}`;
    }

    private closeAllModals(): void {
        this.isWarningVisible = false;
        // Close any open modals
        $('.modal').modal('hide');
        $('.modal').data('bs.modal', null);
    }
}
